import { useNavigate } from "react-router-dom"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import Pill from "@/components/Pill/Pill"
import { ACTION_GLYPH, BinGlyph, BoxGlyph } from "@/components/RemoveDialog/removeGlyphs"
import ProgressBar from "@/components/ProgressBar/ProgressBar"
import Tooltip from "@/components/Tooltip/Tooltip"
import StatusDot from "@/components/StatusDot/StatusDot"
import { ARCHIVED_BADGE, removeActionLabel } from "@/content/archive"
import { ROUND_COPY } from "@/content/rounds"
import { UNTITLED_SESSION } from "@/content/customers"
import { useSessionParticipants } from "@/hooks/useSessionParticipants"
import { getProgress } from "@/store/selectors"
import { relativeTime, runDuration } from "@/utils/relativeTime"
import { reportPath } from "@/utils/routePaths"
import { sessionState } from "@/utils/sessionLifecycle"
import { sessionRemoval } from "@/utils/removal"
import type { FamilyNumber } from "@/theme/colors"
import type { Customer, RemovalKind, Session } from "@/types"
import styles from "@/styles/pages/CustomerDetails.module.css"
import { useJobCardStats } from "./customerDetailsData"

/**
 * One session, as a card.
 *
 * A finished session shows what the room produced and offers the report; an
 * unfinished one shows where it stopped and offers to go back to it. Both ask
 * `sessionState` rather than reading `status` directly — an `active` session past
 * its deadline is not running, and a row that tested status alone would say it
 * was for ever.
 *
 * A card can also be showing a session that is out of the lists, which only
 * happens on an archived CUSTOMER's page — see the session list in
 * `CustomerDetails`. It gets the badge and loses its own Archive and Delete:
 * there is nothing on this session to undo, and restoring the customer is what
 * brings it back.
 */
export default function SessionCard({
  session,
  customer,
  facilitatorName,
  onOpen,
  onRemove,
}: {
  session: Session
  /** The page's customer: an inherited archive is only readable off it. */
  customer: Customer | null
  facilitatorName: string | null
  onOpen: () => void
  /** Omitted where the controls do not belong — an inherited archive. */
  onRemove?: (kind: RemovalKind) => void
}) {
  const navigate = useNavigate()
  const state = sessionState(session)
  const gone = sessionRemoval(session, customer).state
  const done = state === "complete"
  const percent = Math.round(getProgress(session).ratio * 100)
  const participants = useSessionParticipants(session.id)
  const cardStats = useJobCardStats(session.id, done)

  return (
    <div className={styles.card}>
      <div className={styles.cardMain}>
        <button
          type="button"
          className={styles.cardOpen}
          aria-label={session.name || UNTITLED_SESSION}
          onClick={onOpen}
        />
        <span className={styles.cardTop}>
          <StatusDot state={state} />
          {gone !== "active" ? <Pill tone="warning">{ARCHIVED_BADGE[gone]}</Pill> : null}
          <span className={styles.when}>{relativeTime(session.updatedAt)}</span>
        </span>
        <span className={styles.cardTitle}>{session.name || UNTITLED_SESSION}</span>

        {done ? (
          <dl className={styles.cardStats}>
            <div className={styles.cardStat}>
              <dt>Ran</dt>
              <dd>{runDuration(session.startedAt, session.completedAt)}</dd>
            </div>
            <div className={styles.cardStat}>
              <dt>Facilitator</dt>
              <dd>{facilitatorName ?? "—"}</dd>
            </div>
            <div className={styles.cardStat}>
              <dt>Cards</dt>
              <dd>{cardStats ? `${cardStats.drafted} drafted · ${cardStats.kept} kept` : "—"}</dd>
            </div>
          </dl>
        ) : null}
      </div>

      <div className={styles.cardFoot}>
        {!done ? <ProgressBar label={ROUND_COPY[session.round].label} percent={percent} /> : null}

        <div className={styles.cardFootRow}>
          <div className={styles.cardPeople}>
            <AvatarStack
              people={participants.map((person, index) => ({
                id: person.id,
                name: person.name || "Guest",
                family: ((index % 7) + 1) as FamilyNumber,
              }))}
              size={28}
              max={4}
            />
            {participants.length > 0 ? (
              <span className={styles.cardPeopleCount}>{participants.length}</span>
            ) : null}
          </div>

          <span className={styles.cardActions}>
            {/* Quiet, and left of the card's real action. A live session may be
                archived or deleted from here — the dialog behind this counts the
                room first and says what stops. */}
            {onRemove ? (
              <>
                <Tooltip
                  label={removeActionLabel("archived", session.name || UNTITLED_SESSION)}
                  placement="top"
                >
                  <button
                    type="button"
                    className={styles.cardQuiet}
                    aria-label={removeActionLabel("archived", session.name || UNTITLED_SESSION)}
                    onClick={() => onRemove("archived")}
                  >
                    <BoxGlyph size={ACTION_GLYPH} />
                  </button>
                </Tooltip>
                <Tooltip
                  label={removeActionLabel("deleted", session.name || UNTITLED_SESSION)}
                  placement="top"
                >
                  <button
                    type="button"
                    className={`${styles.cardQuiet} ${styles.cardQuietDelete}`}
                    aria-label={removeActionLabel("deleted", session.name || UNTITLED_SESSION)}
                    onClick={() => onRemove("deleted")}
                  >
                    <BinGlyph size={ACTION_GLYPH} />
                  </button>
                </Tooltip>
              </>
            ) : null}

            {done ? (
              <button
                type="button"
                className={styles.cardAction}
                onClick={() => navigate(reportPath(session.id))}
              >
                Download report
              </button>
            ) : (
              <button type="button" className={styles.cardAction} onClick={onOpen}>
                Resume
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
